import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // SPA pageview. gtag is a no-op until the CookieConsent module loads the
    // GA library (only if the user granted analytics), so it's safe to call
    // unconditionally — Consent Mode ensures denial is respected either way.
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname, search]);
  return null;
}
import { AuthProvider } from "./contexts/AuthProvider";
import { ToastProvider } from "./components/common/Toast";
import { cssVariables } from "./lib/theme";
import DialogProvider from "./contexts/DialogContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import GoogleAccountCompletionWrapper from "./components/auth/GoogleAccountCompletionWrapper";
import AppMonitor from "./components/common/AppMonitor";
import CookieConsent from "./components/common/CookieConsent";

// ─── Eagerly loaded (small / used on every route) ─────────────────────────
import Home from "./pages/Home";
import ErrorPage from "./pages/ErrorPage";

// ─── Lazily loaded (heavy pages) ─────────────────────────────────────────
const SearchResults        = lazy(() => import("./pages/SearchResults"));
const SalonLanding         = lazy(() => import("./pages/SalonLanding"));
const SalonDetail          = lazy(() => import("./pages/SalonDetail"));
const UserDashboard        = lazy(() => import("./pages/UserDashboard"));
const AcceptInvite         = lazy(() => import("./pages/AcceptInvite"));
const ConfirmEmail         = lazy(() => import("./pages/ConfirmEmail"));
const BookingConfirmation  = lazy(() => import("./pages/BookingConfirmation"));
const BookingSuccess       = lazy(() => import("./pages/BookingSuccess"));
const EmployeeSelection    = lazy(() => import("./pages/EmployeeSelection"));
const DateTimeSelection    = lazy(() => import("./pages/DateTimeSelection"));
const Gallery              = lazy(() => import("./pages/Gallery"));
const GallerySingleView      = lazy(() => import("./pages/GallerySingleView"));
const PrivacyPolicy        = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService       = lazy(() => import("./pages/TermsOfService"));
const BookingRules         = lazy(() => import("./pages/BookingRules"));
const ContentPolicy        = lazy(() => import("./pages/ContentPolicy"));
const EmployeePublicProfile = lazy(() => import("./pages/EmployeePublicProfile"));
const About                = lazy(() => import("./pages/About"));
const AttractClients       = lazy(() => import("./pages/AttractClients"));
const CalendarFeature      = lazy(() => import("./pages/CalendarFeature"));
const WebsiteFeature       = lazy(() => import("./pages/WebsiteFeature"));
const Sitemap              = lazy(() => import("./pages/Sitemap"));
const MapPage               = lazy(() => import("./pages/MapPage"));

const DeleteAccountRequest = lazy(() => import("./pages/DeleteAccountRequest"));
// ─── Minimal full-screen fallback (no spinner component dependency) ───────
function PageFallback() {
  return (
    <div
      className="page-fallback"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #dde4f5 0%, #e8e4f5 40%, #d8dff0 100%)",
      }}
    >
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          border: "3px solid rgba(179,157,219,0.2)",
          borderTopColor: "#b39ddb",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <style>{`
        .page-fallback { min-height: 100vh; min-height: 100dvh; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: "2rem", textAlign: "center" }}>
          Възникна грешка. Моля, презаредете страницата.
        </div>
      }
    >
      {/* Inject CSS custom properties + global font once */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=Playfair+Display:wght@700;800;900&display=swap&subset=latin,cyrillic,cyrillic-ext,latin-ext');
        html, body { font-family: 'DM Sans', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
        .font-sans { font-family: 'DM Sans', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important; }
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
        ${cssVariables}
      `}</style>

      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <DialogProvider>
            <ToastProvider>
              <AuthProvider>
                <GoogleAccountCompletionWrapper>
                  <AppMonitor />
                  <main>
                  <Suspense fallback={<PageFallback />}>
                    <Routes>
                      {/* Eagerly loaded */}
                      <Route path="/" element={<Home />} />

                      {/* Lazily loaded */}
                     <Route path="/search"                   element={<SearchResults />} />
                      <Route path="/map"                      element={<MapPage />} />
                      <Route path="/about"                    element={<About />} />
                      <Route path="/salon/:salon_id"          element={<SalonDetail />} />
                      <Route path="/addsalon"                 element={<SalonLanding />} />
                      <Route path="/accept-invite/:token"     element={<AcceptInvite />} />
                      <Route path="/confirm-email/:token"     element={<ConfirmEmail />} />
                      <Route path="/booking/employee-selection" element={<EmployeeSelection />} />
                      <Route path="/booking/datetime-selection" element={<DateTimeSelection />} />
                      <Route path="/booking/confirmation"     element={<BookingConfirmation />} />
                      <Route path="/booking/success"          element={<BookingSuccess />} />
                      <Route path="/employee/:employee_id"    element={<EmployeePublicProfile />} />
                      <Route path="/gallery"                  element={<Gallery />} />
                      <Route path="/gallery/:photoId"         element={<GallerySingleView />} />
                      <Route path="/privacy"                  element={<PrivacyPolicy />} />
                      <Route path="/delete-account"           element={<DeleteAccountRequest />} />
                      <Route path="/terms"                    element={<TermsOfService />} />
                      <Route path="/booking-rules"             element={<BookingRules />} />
                      <Route path="/content-policy"            element={<ContentPolicy />} />

                      {/* Footer platform pages */}
                      <Route path="/attract-clients"          element={<AttractClients />} />
                      <Route path="/calendar"                 element={<CalendarFeature />} />
                      <Route path="/website"                  element={<WebsiteFeature />} />
                      <Route path="/sitemap"                  element={<Sitemap />} />

                      <Route
                        path="/userdashboard"
                        element={
                          <ProtectedRoute requireUser>
                            <UserDashboard />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="*" element={<ErrorPage />} />
                    </Routes>
                  </Suspense>
                  </main>
                </GoogleAccountCompletionWrapper>
              </AuthProvider>
              <CookieConsent />
            </ToastProvider>
          </DialogProvider>
        </Router>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;